import { useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Skeleton, Chip, useMediaQuery, useTheme,
} from '@mui/material';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import {
  AttachMoney, People, DirectionsCar, LocalShipping, HomeRepairService, DragIndicator,
} from '@mui/icons-material';
import StatCard from '../ui/StatCard';
import { useTenantCurrency } from '../../hooks/useTenantCurrency';
import { DASHBOARD_METRIC_NAV } from '../../config/dashboardNavigation';
import type { DashboardDefinition, DashboardWidget } from '../../services/platform/runtimeService';

const METRIC_ICONS: Record<string, React.ReactNode> = {
  totalUsers: <People />,
  totalRides: <DirectionsCar />,
  completedRides: <DirectionsCar />,
  totalDrivers: <People />,
  totalPorterOrders: <LocalShipping />,
  completedPorterOrders: <LocalShipping />,
  totalOnDemandBookings: <HomeRepairService />,
  completedOnDemandBookings: <HomeRepairService />,
  activeOnDemandBookings: <HomeRepairService />,
  totalRevenue: <AttachMoney />,
};

interface DashboardRendererProps {
  dashboard?: DashboardDefinition | null;
  overview?: Record<string, number>;
  analytics?: Record<string, unknown>;
  dashboardData?: Record<string, unknown>;
  loading?: boolean;
  editable?: boolean;
  onLayoutChange?: (widgetId: string, x: number, y: number) => void;
}

function WidgetView({
  widget, overview = {}, analytics, dashboardData, loading, editable, onDrag,
}: {
  widget: DashboardWidget;
  overview?: Record<string, number>;
  analytics?: Record<string, unknown>;
  dashboardData?: Record<string, unknown>;
  loading?: boolean;
  editable?: boolean;
  onDrag?: (e: React.DragEvent) => void;
}) {
  const { formatAmount } = useTenantCurrency();
  const wrap = (node: React.ReactNode) => (
    <Box position="relative" height="100%">
      {editable && (
        <Box
          draggable
          onDragStart={onDrag}
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 2, cursor: 'grab', opacity: 0.6 }}
        >
          <DragIndicator fontSize="small" />
        </Box>
      )}
      {node}
    </Box>
  );

  if (widget.type === 'stat') {
    const raw = widget.metricKey ? overview[widget.metricKey] : 0;
    const value = widget.metricKey === 'totalRevenue' ? formatAmount(Number(raw || 0)) : raw ?? 0;
    return wrap(
      <StatCard
        title={widget.title}
        value={value}
        icon={METRIC_ICONS[widget.metricKey || ''] || <AttachMoney />}
        loading={loading}
        to={widget.metricKey ? DASHBOARD_METRIC_NAV[widget.metricKey] : undefined}
      />
    );
  }

  if (widget.type === 'chart_line') {
    const dataKey = (widget.config?.dataKey as string) || 'rideStats';
    const stats = (analytics?.[dataKey] as { _id: string; count: number; revenue: number }[]) || [];
    const chartData = stats.map((r) => ({ date: r._id, count: r.count, revenue: r.revenue }));
    return wrap(
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>{widget.title}</Typography>
          <Box sx={{ height: 220 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#2563eb" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (widget.type === 'list') {
    const listKey = (widget.config?.listKey as string) || 'recentRides';
    const labelKey = (widget.config?.labelKey as string) || 'rideNumber';
    const items = ((dashboardData?.[listKey] as Record<string, unknown>[]) || []).slice(0, 5);
    return wrap(
      <Card sx={{ height: '100%' }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>{widget.title}</Typography>
          {items.map((item) => (
            <Box key={String(item._id)} display="flex" justifyContent="space-between" py={1} borderBottom={1} borderColor="divider">
              <Typography variant="body2" fontWeight={600}>{String(item[labelKey] || item._id)}</Typography>
              {item.status != null && <Chip label={String(item.status)} size="small" />}
            </Box>
          ))}
          {!items.length && <Typography variant="caption" color="text.secondary">No items</Typography>}
        </CardContent>
      </Card>
    );
  }

  return wrap(
    <Card sx={{ height: '100%' }}>
      <CardContent>
        <Typography variant="subtitle2">{widget.title}</Typography>
      </CardContent>
    </Card>
  );
}

export default function DashboardRenderer({
  dashboard, overview, analytics, dashboardData, loading, editable, onLayoutChange,
}: DashboardRendererProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const widgetMap = useMemo(() => {
    const map = new Map<string, DashboardWidget>();
    (dashboard?.widgets || []).forEach((w) => map.set(w.id, w));
    return map;
  }, [dashboard]);

  if (!dashboard?.layout?.length) return null;

  const handleGridDrop = (e: React.DragEvent, col: number, row: number) => {
    e.preventDefault();
    const widgetId = e.dataTransfer.getData('widgetId');
    if (widgetId && onLayoutChange) onLayoutChange(widgetId, col, row);
  };

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : isTablet ? 'repeat(6, 1fr)' : 'repeat(12, 1fr)',
        gridAutoRows: 'minmax(72px, auto)',
        gap: { xs: 1.5, md: 2 },
        mb: 3,
      }}
      onDragOver={(e) => editable && e.preventDefault()}
    >
      {dashboard.layout.map((item) => {
        const widget = widgetMap.get(item.i);
        if (!widget) return null;
        const colSpan = isMobile ? 1 : isTablet ? Math.min(item.w, 6) : item.w;
        const colStart = isMobile ? 1 : isTablet ? Math.min(item.x, 5) + 1 : item.x + 1;
        return (
          <Box
            key={item.i}
            draggable={false}
            onDragOver={(e) => editable && e.preventDefault()}
            onDrop={(e) => editable && handleGridDrop(e, item.x, item.y)}
            sx={{
              gridColumn: isMobile ? '1 / -1' : `${colStart} / span ${colSpan}`,
              gridRow: isMobile ? 'auto' : `${item.y + 1} / span ${item.h}`,
              minHeight: isMobile ? 120 : item.h * 72,
              outline: editable ? '1px dashed' : 'none',
              outlineColor: 'divider',
              borderRadius: 2,
            }}
          >
            <WidgetView
              widget={widget}
              overview={overview}
              analytics={analytics}
              dashboardData={dashboardData}
              loading={loading}
              editable={editable}
              onDrag={(e) => e.dataTransfer.setData('widgetId', item.i)}
            />
          </Box>
        );
      })}
    </Box>
  );
}
